import React from 'react';
import {
    AdvancedSearchListItem,
    SellerSearchListItem,
} from './suggestion-list-items';
import { toSearchString } from 'tradera/utils/url';
import {
    useCurrentLanguage,
    useTranslator,
    useUrlLocalizer,
} from 'tradera/lang/translate';
import { KEYCODE } from '../search';
import { useGetTrendingSearchesQuery } from 'tradera/state/services/discover-api';
import { Chip } from 'tradera/components/chip/chip';

const preventDefault = (e) => {
    e.preventDefault();
    e.stopPropagation();
};

const mapTrendingSearchesToTags = (trendingSearches) =>
    trendingSearches?.map((search) => ({
        index: search.index,
        name: search.query || '',
        url: `/search?q=${search.query}${
            search.categoryId ? `&categoryId=${search.categoryId}` : ''
        }&sortBy=MostBids`,
    }));

export const SearchSuggestionSection = ({
    suggestions,
    onPreviousSearchRemoved,
    searchTerm,
    changeSearchTerm,
}) => {
    const { t } = useTranslator();
    const { toLocalizedUrl } = useUrlLocalizer();
    const { language } = useCurrentLanguage();

    const previousSearches = suggestions.filter(
        (entry) => entry.type === 'previous',
    );

    const { data: trendingSearchesItems } = useGetTrendingSearchesQuery({
        languageCodeIso2: language,
    });

    const trendingSearches = React.useMemo(
        () => mapTrendingSearchesToTags(trendingSearchesItems),
        [trendingSearchesItems],
    );

    const previousSearchesEmpty =
        !previousSearches || previousSearches.length === 0;
    const trendingSearchesEmpty =
        !trendingSearches || trendingSearches.length === 0;

    const sectionEmpty = previousSearchesEmpty && trendingSearchesEmpty;

    const removePreviousSearch = (e, searchTerm) => {
        if (e.type === 'click' || e.which === KEYCODE.ENTER) {
            preventDefault(e);
            onPreviousSearchRemoved(searchTerm);
        }
    };

    return (
        <>
            {!sectionEmpty && (
                <div className="px-2 pt-2 d-flex flex-column">
                    {!previousSearchesEmpty && (
                        <>
                            <p className="font-weight-bold mb-1">
                                {t('siteHeader_recentSearches')}
                            </p>
                            <div className="mb-1 d-flex flex-wrap gap-1">
                                {previousSearches.map((previousSearch) => (
                                    <Chip
                                        key={previousSearch.key}
                                        href={toLocalizedUrl(
                                            `/search${toSearchString(
                                                previousSearch.queryParams,
                                            )}`,
                                        )}
                                        onClick={(e) => {
                                            changeSearchTerm(
                                                previousSearch.text,
                                                e,
                                            );
                                        }}
                                        onRemove={(e) => {
                                            removePreviousSearch(
                                                e,
                                                previousSearch.text,
                                            );
                                        }}
                                        onMouseDown={(e) => e.preventDefault()}
                                        title={previousSearch.text}
                                        data-suggestion-section-button
                                    />
                                ))}
                            </div>
                        </>
                    )}
                    {!trendingSearchesEmpty && (
                        <>
                            <p className="font-weight-bold mb-1">
                                {t('siteHeader_trendingSearches')}
                            </p>
                            <div className="mb-1 d-flex flex-wrap gap-1">
                                {trendingSearches.map((trendingSearch) => (
                                    <Chip
                                        key={trendingSearch.name}
                                        href={toLocalizedUrl(
                                            trendingSearch.url,
                                        )}
                                        onClick={(e) => {
                                            changeSearchTerm(
                                                trendingSearch.name,
                                                e,
                                            );
                                        }}
                                        onMouseDown={(e) => e.preventDefault()}
                                        data-suggestion-section-button
                                        title={trendingSearch.name}
                                    />
                                ))}
                            </div>
                        </>
                    )}
                </div>
            )}
            <div className="list-group">
                {suggestions.map((entry) => {
                    switch (entry.type) {
                        case 'link-advanced-search':
                            return (
                                <AdvancedSearchListItem
                                    key={entry.key}
                                    changeSearchTerm={changeSearchTerm}
                                    searchTerm={searchTerm}
                                />
                            );
                        case 'link-seller-search':
                            return (
                                <SellerSearchListItem
                                    key={entry.key}
                                    changeSearchTerm={changeSearchTerm}
                                    searchTerm={searchTerm}
                                />
                            );
                        default:
                            return null;
                    }
                })}
            </div>
        </>
    );
};
